import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ArticlePage() {
  const { slug } = useParams();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const contentAnimation = useScrollAnimation({ threshold: 0.2 });
  const shareAnimation = useScrollAnimation({ threshold: 0.3 });

  const articles: Record<string, any> = {
    'revaloriza-tu-inmueble': {
      title: 'Revaloriza tu Inmueble: La Inversión que Siempre Gana',
      subtitle: 'Por qué remodelar tu hogar es la mejor decisión financiera',
      image: 'https://public.readdy.ai/ai/img_res/f250c997248132c121c727bb41bd54e5.jpg',
      date: '15 Enero 2024',
      readTime: '5 min',
      category: 'Inversión Inmobiliaria',
      content: `
        <h2>La Mejor Inversión para tu Patrimonio</h2>
        <p>Cuando pensamos en inversiones, muchas veces miramos hacia el mercado de valores, negocios o cuentas de ahorro. Sin embargo, una de las inversiones más seguras y rentables está justo frente a nosotros: <strong>nuestro propio inmueble</strong>.</p>
        
        <h3>¿Por Qué Remodelar Aumenta el Valor?</h3>
        <p>Una remodelación bien planificada puede aumentar el valor de tu propiedad entre un <strong>15% y 40%</strong>, dependiendo del tipo de mejoras realizadas. Esto significa que cada dólar invertido inteligentemente puede multiplicarse significativamente.</p>
        
        <h3>Los Arreglos que Más Valorizan</h3>
        <ul>
          <li><strong>Cocina Moderna:</strong> La cocina es el corazón del hogar. Una cocina renovada puede aumentar el valor hasta un 20%.</li>
          <li><strong>Baños Actualizados:</strong> Baños modernos y funcionales son altamente valorados por compradores potenciales.</li>
          <li><strong>Pintura y Acabados:</strong> Una inversión pequeña con gran impacto visual y de valor.</li>
          <li><strong>Sistemas Eléctricos y Plomería:</strong> Mejoras fundamentales que dan seguridad y tranquilidad.</li>
          <li><strong>Espacios Exteriores:</strong> Jardines, terrazas y patios bien cuidados aumentan significativamente el atractivo.</li>
        </ul>
        
        <h3>Casos Reales en Ecuador</h3>
        <p>En Otavalo, una familia invirtió $8,000 en remodelar su cocina y baños. Al vender su propiedad dos años después, obtuvieron $15,000 adicionales sobre el precio de mercado original. <strong>Eso es casi el doble de retorno de inversión.</strong></p>
        
        <h3>Planificación: La Clave del Éxito</h3>
        <p>No todas las remodelaciones son iguales. Es fundamental:</p>
        <ul>
          <li>Establecer un presupuesto realista</li>
          <li>Priorizar arreglos fundamentales antes que estéticos</li>
          <li>Trabajar con profesionales verificados</li>
          <li>Anticipar fechas de inicio y finalización</li>
          <li>Mantener comunicación constante con tu equipo</li>
        </ul>
        
        <h3>HogarSoluciones: Tu Aliado en la Revalorización</h3>
        <p>En <strong>HogarSoluciones Ecuador</strong>, entendemos que cada peso invertido cuenta. Por eso:</p>
        <ul>
          <li>Te conectamos con técnicos profesionales verificados</li>
          <li>Coordinamos todo el proyecto por ti</li>
          <li>Ofrecemos presupuestos transparentes y accesibles</li>
          <li>Respaldamos cada trabajo con garantía</li>
          <li>Estamos disponibles 24/7 para asesorarte</li>
        </ul>
        
        <h3>El Momento es Ahora</h3>
        <p>El mercado inmobiliario ecuatoriano está en constante crecimiento. Las propiedades bien mantenidas y modernizadas se venden más rápido y a mejores precios. <strong>No esperes más para revalorizar tu patrimonio.</strong></p>
        
        <div class="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-lg">
          <h4 class="text-xl font-bold text-gray-900 mb-3">💡 Consejo de Experto</h4>
          <p class="text-gray-700 leading-relaxed">Antes de iniciar cualquier remodelación, consulta con nuestros asesores. Te ayudaremos a identificar qué mejoras darán el mayor retorno de inversión según tu presupuesto y objetivos.</p>
        </div>
        
        <h3>Conclusión</h3>
        <p>Remodelar tu inmueble no es un gasto, es una <strong>inversión inteligente</strong> que aumenta tu patrimonio, mejora tu calidad de vida y te da tranquilidad para el futuro. Con la planificación correcta y el equipo adecuado, los resultados superarán tus expectativas.</p>
      `
    },
    'olvidate-del-estres': {
      title: 'Olvídate del Estrés: Nosotros Coordinamos Todo por Ti',
      subtitle: 'Cómo HogarSoluciones simplifica tu proyecto de remodelación',
      image: 'https://readdy.ai/api/search-image?query=happy%20relaxed%20homeowner%20sitting%20comfortably%20while%20professional%20construction%20team%20works%20in%20background%20stress%20free%20home%20renovation%20experience%20ecuadorian%20setting%20natural%20lighting%20professional%20photography&width=1200&height=600&seq=blog-estres-full-001&orientation=landscape',
      date: '12 Enero 2024',
      readTime: '4 min',
      category: 'Gestión de Proyectos',
      content: `
        <h2>El Dolor de Cabeza de las Remodelaciones Tradicionales</h2>
        <p>Todos hemos escuchado historias de terror sobre remodelaciones: técnicos que no llegan, trabajos mal hechos, presupuestos que se duplican, materiales equivocados, y la lista continúa. <strong>El estrés de coordinar una remodelación puede ser abrumador.</strong></p>
        
        <h3>Los Problemas Más Comunes</h3>
        <ul>
          <li><strong>Buscar técnicos confiables:</strong> ¿A quién llamar? ¿Cómo saber si son buenos?</li>
          <li><strong>Coordinar múltiples profesionales:</strong> Albañil, electricista, plomero, pintor... ¿Quién va primero?</li>
          <li><strong>Supervisar el trabajo:</strong> ¿Están haciendo bien su trabajo? ¿Cuándo terminan?</li>
          <li><strong>Manejar imprevistos:</strong> Problemas inesperados que surgen durante la obra.</li>
          <li><strong>Comunicación constante:</strong> Llamadas, mensajes, visitas... consume mucho tiempo.</li>
        </ul>
        
        <h3>La Solución HogarSoluciones</h3>
        <p>Imagina poder <strong>delegar toda la coordinación</strong> a un equipo profesional que se encarga de absolutamente todo. Eso es exactamente lo que hacemos.</p>
        
        <h3>Cómo Funciona Nuestro Servicio</h3>
        
        <h4>1. Consulta Inicial (Sin Costo)</h4>
        <p>Conversamos contigo para entender exactamente qué necesitas. Puedes contactarnos por WhatsApp, llamada o chat 24/7. <strong>Respondemos inmediatamente.</strong></p>
        
        <h4>2. Evaluación y Presupuesto</h4>
        <p>Visitamos tu inmueble, evaluamos el trabajo necesario y te presentamos un presupuesto detallado y transparente. Sin sorpresas.</p>
        
        <h4>3. Selección de Profesionales</h4>
        <p>De nuestra red de técnicos verificados, seleccionamos al equipo perfecto para tu proyecto. Todos con experiencia comprobada y referencias reales.</p>
        
        <h4>4. Coordinación Total</h4>
        <p>Nosotros coordinamos:</p>
        <ul>
          <li>Fechas y horarios de trabajo</li>
          <li>Secuencia de actividades</li>
          <li>Compra y entrega de materiales</li>
          <li>Supervisión diaria del trabajo</li>
          <li>Solución de imprevistos</li>
        </ul>
        
        <h4>5. Comunicación Constante</h4>
        <p>Te mantenemos informado del progreso sin que tengas que preocuparte por nada. Fotos, actualizaciones y disponibilidad 24/7.</p>
        
        <h4>6. Entrega y Garantía</h4>
        <p>Revisamos que todo esté perfecto antes de la entrega final. Todos nuestros trabajos tienen garantía.</p>
        
        <h3>Testimonios Reales</h3>
        
        <div class="bg-gray-50 p-6 rounded-lg my-6">
          <p class="text-gray-700 italic mb-3">"Necesitaba remodelar mi oficina pero no tenía tiempo para coordinar. HogarSoluciones se encargó de todo. Solo tuve que aprobar el presupuesto y ellos hicieron el resto. Quedó perfecta y sin estrés."</p>
          <p class="text-gray-900 font-semibold">- María González, Quito</p>
        </div>
        
        <div class="bg-gray-50 p-6 rounded-lg my-6">
          <p class="text-gray-700 italic mb-3">"Vivo en el extranjero y necesitaba arreglar mi casa en Otavalo para rentarla. Germán y su equipo coordinaron todo a distancia. Excelente comunicación y trabajo impecable."</p>
          <p class="text-gray-900 font-semibold">- Carlos Mendoza, Miami</p>
        </div>
        
        <h3>Atención 24/7: Siempre Disponibles</h3>
        <p>Entendemos que las dudas y emergencias no tienen horario. Por eso ofrecemos:</p>
        <ul>
          <li><strong>Chat de voz y texto 24/7:</strong> Respuestas inmediatas en cualquier momento</li>
          <li><strong>WhatsApp directo:</strong> Comunicación rápida y efectiva</li>
          <li><strong>Llamadas:</strong> Habla directamente con nuestros asesores</li>
        </ul>
        
        <h3>Presupuestos para Cada Bolsillo</h3>
        <p>No importa si tu proyecto es pequeño, mediano o grande. Tenemos soluciones accesibles para cada presupuesto. <strong>Desde reparaciones menores hasta remodelaciones completas.</strong></p>
        
        <div class="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-lg">
          <h4 class="text-xl font-bold text-gray-900 mb-3">🎯 Ventaja Clave</h4>
          <p class="text-gray-700 leading-relaxed">Al trabajar con HogarSoluciones, no solo ahorras tiempo y estrés, también ahorras dinero. Nuestras relaciones con proveedores nos permiten obtener mejores precios en materiales, y nuestros técnicos trabajan de manera más eficiente al estar bien coordinados.</p>
        </div>
        
        <h3>Respaldo Profesional</h3>
        <p>Contamos con el respaldo de:</p>
        <ul>
          <li>Ingenieros civiles</li>
          <li>Arquitectos</li>
          <li>Decoradores de interiores</li>
          <li>Técnicos especializados certificados</li>
        </ul>
        
        <h3>Conclusión</h3>
        <p>Tu tiempo es valioso. Tu tranquilidad es importante. <strong>Deja que nosotros nos encarguemos del estrés de coordinar tu remodelación.</strong> Tú solo disfruta del resultado final.</p>
        
        <p class="text-lg font-semibold text-teal-600 mt-6">¿Listo para una remodelación sin estrés? Contáctanos ahora y conversemos sobre tu proyecto.</p>
      `
    },
    'planifica-inteligentemente': {
      title: 'Planifica Inteligentemente: De la Idea a la Realidad',
      subtitle: 'Guía completa para tu proyecto de remodelación sin sorpresas',
      image: 'https://readdy.ai/api/search-image?query=architect%20and%20homeowner%20reviewing%20detailed%20renovation%20blueprints%20and%20plans%20on%20table%20with%20laptop%20calculator%20and%20design%20materials%20professional%20planning%20meeting%20ecuadorian%20office%20setting%20bright%20natural%20lighting&width=1200&height=600&seq=blog-planifica-full-001&orientation=landscape',
      date: '10 Enero 2024',
      readTime: '6 min',
      category: 'Planificación',
      content: `
        <h2>La Planificación: El Secreto del Éxito</h2>
        <p>Una remodelación exitosa no comienza con el primer martillazo, comienza con una <strong>planificación inteligente</strong>. La diferencia entre un proyecto que sale bien y uno que se convierte en pesadilla está en los detalles que se consideran antes de empezar.</p>
        
        <h3>Paso 1: Define tu Visión</h3>
        <p>Antes de contactar a cualquier técnico, necesitas claridad sobre:</p>
        <ul>
          <li><strong>¿Qué quieres lograr?</strong> Modernizar, ampliar, reparar, cambiar estilo...</li>
          <li><strong>¿Cuál es tu prioridad?</strong> Funcionalidad, estética, valor de reventa...</li>
          <li><strong>¿Qué te inspira?</strong> Guarda fotos de referencia, estilos que te gustan</li>
          <li><strong>¿Qué no te gusta de tu espacio actual?</strong> Identifica los problemas a resolver</li>
        </ul>
        
        <h3>Paso 2: Establece un Presupuesto Realista</h3>
        <p>El dinero es un factor crucial. Aquí está cómo manejarlo inteligentemente:</p>
        
        <h4>Presupuesto Base</h4>
        <p>Define cuánto puedes invertir cómodamente sin comprometer tus finanzas.</p>
        
        <h4>Fondo de Contingencia (15-20%)</h4>
        <p>Siempre surgen imprevistos. Reserva un 15-20% adicional para cubrirlos sin estrés.</p>
        
        <h4>Priorización</h4>
        <p>Si tu presupuesto es limitado, prioriza:</p>
        <ol>
          <li><strong>Arreglos estructurales y de seguridad</strong> (plomería, electricidad, techos)</li>
          <li><strong>Mejoras funcionales</strong> (cocina, baños)</li>
          <li><strong>Mejoras estéticas</strong> (pintura, decoración)</li>
        </ol>
        
        <h3>Paso 3: Investiga y Cotiza</h3>
        <p>No te quedes con la primera opción. Compara:</p>
        <ul>
          <li>Al menos 3 presupuestos diferentes</li>
          <li>Referencias y trabajos anteriores</li>
          <li>Tiempo estimado de ejecución</li>
          <li>Garantías ofrecidas</li>
          <li>Forma de pago</li>
        </ul>
        
        <div class="bg-amber-50 border-l-4 border-amber-500 p-6 my-8 rounded-lg">
          <h4 class="text-xl font-bold text-gray-900 mb-3">⚠️ Señal de Alerta</h4>
          <p class="text-gray-700 leading-relaxed">Desconfía de presupuestos extremadamente bajos. Muchas veces esconden costos ocultos o trabajo de baja calidad. Un precio justo refleja materiales de calidad y mano de obra profesional.</p>
        </div>
        
        <h3>Paso 4: Anticipa la Fecha de Inicio</h3>
        <p>Este es un punto <strong>CRUCIAL</strong> que muchos pasan por alto:</p>
        
        <h4>¿Por Qué es Importante?</h4>
        <ul>
          <li>Los buenos técnicos tienen agenda ocupada</li>
          <li>Algunos materiales requieren tiempo de pedido</li>
          <li>Coordinar múltiples profesionales necesita planificación</li>
          <li>Evita retrasos y sobrecostos</li>
        </ul>
        
        <h4>Cómo Hacerlo</h4>
        <p>Cuando contactes a HogarSoluciones, <strong>anticípanos tu fecha ideal de inicio</strong>. Esto nos permite:</p>
        <ul>
          <li>Reservar a los mejores técnicos para tu proyecto</li>
          <li>Coordinar la compra de materiales con anticipación</li>
          <li>Planificar la secuencia de trabajos eficientemente</li>
          <li>Garantizar que tu proyecto comience a tiempo</li>
        </ul>
        
        <h3>Paso 5: Elige a los Profesionales Correctos</h3>
        <p>No todos los técnicos son iguales. Busca:</p>
        
        <h4>Experiencia Verificable</h4>
        <ul>
          <li>Años en el oficio</li>
          <li>Proyectos similares completados</li>
          <li>Fotos de trabajos anteriores</li>
        </ul>
        
        <h4>Referencias Reales</h4>
        <ul>
          <li>Contactos de clientes anteriores</li>
          <li>Testimonios verificables</li>
          <li>Reputación en la comunidad</li>
        </ul>
        
        <h4>Certificaciones</h4>
        <ul>
          <li>Títulos técnicos</li>
          <li>Certificados de capacitación</li>
          <li>Membresías profesionales</li>
        </ul>
        
        <h3>Paso 6: Formaliza Todo por Escrito</h3>
        <p>Un acuerdo verbal no es suficiente. <strong>Todo debe estar documentado:</strong></p>
        
        <h4>El Contrato Debe Incluir:</h4>
        <ul>
          <li>Descripción detallada del trabajo</li>
          <li>Costo total desglosado</li>
          <li>Forma y calendario de pagos</li>
          <li>Fecha de inicio y finalización estimada</li>
          <li>Responsabilidades de cada parte</li>
          <li>Garantías ofrecidas</li>
          <li>Procedimiento para cambios o imprevistos</li>
        </ul>
        
        <div class="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-lg">
          <h4 class="text-xl font-bold text-gray-900 mb-3">✅ Ventaja HogarSoluciones</h4>
          <p class="text-gray-700 leading-relaxed">En HogarSoluciones, todos nuestros proyectos se formalizan con un acuerdo contractual que protege a todas las partes: cliente, técnico y empresa. Transparencia total desde el inicio.</p>
        </div>
        
        <h3>Paso 7: Prepara tu Espacio</h3>
        <p>Antes de que lleguen los técnicos:</p>
        <ul>
          <li>Retira objetos de valor y frágiles</li>
          <li>Protege muebles que no se pueden mover</li>
          <li>Asegura acceso fácil al área de trabajo</li>
          <li>Informa a vecinos si habrá ruido</li>
          <li>Designa un área para materiales y herramientas</li>
        </ul>
        
        <h3>Paso 8: Mantén Comunicación Constante</h3>
        <p>Durante la ejecución:</p>
        <ul>
          <li>Visitas regulares al sitio (si es posible)</li>
          <li>Comunicación diaria con el coordinador</li>
          <li>Documentación fotográfica del progreso</li>
          <li>Resolución rápida de dudas o cambios</li>
        </ul>
        
        <h3>Paso 9: Inspección Final</h3>
        <p>Antes de dar por terminado el proyecto:</p>
        <ul>
          <li>Revisa cada detalle del trabajo</li>
          <li>Verifica que todo funcione correctamente</li>
          <li>Solicita correcciones si es necesario</li>
          <li>Obtén documentación de garantías</li>
          <li>Guarda facturas y recibos</li>
        </ul>
        
        <h3>Presupuestos para Cada Necesidad</h3>
        
        <h4>Proyectos Pequeños ($500 - $2,000)</h4>
        <ul>
          <li>Reparaciones menores</li>
          <li>Pintura de habitaciones</li>
          <li>Arreglos de plomería o electricidad</li>
        </ul>
        
        <h4>Proyectos Medianos ($2,000 - $8,000)</h4>
        <ul>
          <li>Remodelación de baño o cocina</li>
          <li>Cambio de pisos</li>
          <li>Mejoras en jardín o patio</li>
        </ul>
        
        <h4>Proyectos Grandes ($8,000+)</h4>
        <ul>
          <li>Remodelación completa</li>
          <li>Ampliaciones</li>
          <li>Proyectos comerciales</li>
        </ul>
        
        <h3>El Respaldo Profesional que Necesitas</h3>
        <p>En HogarSoluciones contamos con:</p>
        <ul>
          <li><strong>Ingenieros Civiles:</strong> Para proyectos estructurales</li>
          <li><strong>Arquitectos:</strong> Para diseño y planificación espacial</li>
          <li><strong>Decoradores:</strong> Para acabados y estética</li>
          <li><strong>Técnicos Especializados:</strong> Para cada área específica</li>
        </ul>
        
        <h3>No Somos una Agencia Costosa</h3>
        <p>A pesar de nuestro respaldo profesional, <strong>nuestros precios son accesibles</strong>. ¿Cómo lo logramos?</p>
        <ul>
          <li>Relaciones directas con proveedores</li>
          <li>Eficiencia en la coordinación</li>
          <li>Volumen de proyectos</li>
          <li>Compromiso con hacer accesible la calidad</li>
        </ul>
        
        <h3>Conclusión</h3>
        <p>Una remodelación exitosa es el resultado de una <strong>planificación inteligente</strong>, profesionales calificados y comunicación constante. No dejes tu proyecto al azar.</p>
        
        <p class="text-lg font-semibold text-teal-600 mt-6">Habla con anticipación con nosotros. Nuestros asesores te atenderán gustosos y te guiarán en cada paso del proceso. Desde la idea inicial hasta la entrega final, estaremos contigo.</p>
        
        <div class="bg-gradient-to-r from-teal-50 to-cyan-50 p-8 rounded-xl my-8 text-center">
          <h4 class="text-2xl font-bold text-gray-900 mb-4">¿Listo para Comenzar?</h4>
          <p class="text-gray-700 mb-6">Contáctanos hoy y recibe una consulta gratuita para tu proyecto</p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/593978966415" class="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors">
              <i class="ri-whatsapp-line text-xl mr-2"></i>
              WhatsApp: +593 978 966 415
            </a>
            <a href="tel:+584248082841" class="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors">
              <i class="ri-phone-line text-xl mr-2"></i>
              Llamar: +58 424 808 2841
            </a>
          </div>
        </div>
      `
    }
  };

  const article = slug ? articles[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-semibold">
            Volver al Blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `https://hogarsolucionesec.com/blog/${slug}`;
  const shareText = encodeURIComponent(article.title);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-home-heart-fill text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-teal-600" style={{fontFamily: "Pacifico, serif"}}>
                  HogarSoluciones
                </h1>
                <span className="text-sm font-semibold text-gray-600">Ecuador</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-lg font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                Inicio
              </Link>
              <Link to="/servicios" className="text-lg font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                Servicios
              </Link>
              <Link to="/blog" className="text-lg font-medium text-teal-600 transition-colors cursor-pointer">
                Blog
              </Link>
              <Link to="/contacto" className="text-lg font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                Únete como Técnico
              </Link>
              <a href="/#solicitar" className="bg-teal-600 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-teal-700 whitespace-nowrap cursor-pointer">
                Solicitar Servicio
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-medium">
              ← Volver al Blog
            </Link>
          </div>

          {/* Category and Meta */}
          <div className="mb-6">
            <span className="inline-block bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {article.category}
            </span>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-1"></i>
                {article.date}
              </span>
              <span className="flex items-center">
                <i className="ri-time-line mr-1"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-xl text-teal-600 font-semibold mb-8">
            {article.subtitle}
          </p>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-96 object-cover object-center"
            />
          </div>

          {/* Article Content */}
          <div 
            ref={contentAnimation.elementRef}
            className={`prose prose-lg max-w-none transition-all duration-1000 ${
              contentAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            dangerouslySetInnerHTML={{ __html: article.content }}
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}
          />

          {/* Share Section */}
          <div ref={shareAnimation.elementRef} className={`mt-16 pt-8 border-t border-gray-200 transition-all duration-1000 ${
            shareAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Comparte este artículo</h3>
            <div className="flex flex-wrap gap-4">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl mr-2"></i>
                Facebook
              </a>
              <a
                href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl mr-2"></i>
                WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-fill text-xl mr-2"></i>
                Twitter
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">¿Listo para tu Proyecto?</h3>
            <p className="text-xl mb-6">Contáctanos y recibe asesoría gratuita</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/593978966415"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl mr-3"></i>
                WhatsApp
              </a>
              <Link
                to="/#solicitar"
                className="inline-flex items-center bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-300 transition-colors cursor-pointer"
              >
                <i className="ri-file-list-line text-2xl mr-3"></i>
                Solicitar Presupuesto
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-home-heart-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold" style={{fontFamily: "Pacifico, serif"}}>
                  HogarSoluciones
                </h3>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Técnicos especializados a su servicio, soluciones a su alcance.
              </p>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-4">Síguenos en Redes Sociales</h4>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://www.facebook.com/share/1DAL4GHqeD/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-facebook-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Facebook</p>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hogarsolucionesecuador/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-instagram-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Instagram</p>
                  </a>
                  
                  <a 
                    href="https://wa.me/593978966415" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-whatsapp-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">WhatsApp</p>
                  </a>
                  
                  <a 
                    href="https://www.tiktok.com/@hogarsolucionesecuador" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-tiktok-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">TikTok</p>
                  </a>
                  
                  <a 
                    href="mailto:info@hogarsolucionesecuador.com"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-mail-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Email</p>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Albañilería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Plomería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Electricidad</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white cursor-pointer">Inicio</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white cursor-pointer">Blog</Link></li>
                <li><Link to="/contacto" className="text-gray-400 hover:text-white cursor-pointer">Contacto</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">📧 info@hogarsolucionesecuador.com</li>
                <li>
                  <a href="tel:+593978966415" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +593 978 966 415
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 HogarSoluciones Ecuador. Todos los derechos reservados.
            </p>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}